import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Trash2, RotateCcw } from 'lucide-react';
import QRCode from 'react-qr-code';
import { overlayClose } from '../../lib/overlayClose';

/**
 * Modal: excluir item selecionado via tecla Delete — pede APENAS a senha do
 * operador; ao apertar Enter, exclui o item (registrado no log). Sem motivo.
 */
export const DeleteItemPasswordModal = ({ target, password, setPassword, error, onCancel, onConfirm }) => {
  if (!target) return null;
  const item = target.item || {};
  const fmt = (n) => Number(n || 0).toFixed(2).replace('.', ',');
  const submitOnEnter = (e) => { if (e.key === 'Enter') { e.preventDefault(); onConfirm(); } };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...overlayClose(onCancel)}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-item-modal">
        <h3 className="text-2xl font-bold text-slate-900 mb-1 flex items-center gap-2"><Trash2 size={22} className="text-red-600" /> Excluir Item</h3>
        <p className="text-sm text-slate-600 mb-3">Digite a senha do operador para excluir este item da venda.</p>
        <div className="bg-slate-50 border rounded-md p-3 mb-3">
          <p className="font-bold text-slate-800" data-testid="delete-item-name">{item.product_name}</p>
          <div className="flex justify-between text-sm text-slate-600 mt-1">
            <span>{Number(item.quantity)} x R$ {fmt(item.unit_price)}</span>
            <span className="font-mono font-bold">R$ {fmt(item.subtotal)}</span>
          </div>
        </div>
        <div className="space-y-1 mb-3">
          <Label className="text-xs uppercase tracking-wider font-bold">Senha do operador *</Label>
          <Input
            type="password"
            value={password || ''}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={submitOnEnter}
            placeholder="Digite a senha e pressione Enter"
            autoFocus
            data-testid="delete-item-password"
          />
          <p className="text-[11px] text-slate-500">Pressione <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">Enter</kbd> para excluir.</p>
        </div>
        {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="flex-1" data-testid="delete-item-cancel">Voltar</Button>
          <Button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="delete-item-confirm">
            Excluir item
          </Button>
        </div>
      </div>
    </div>
  );
};


/**
 * Modal: cancelar item individual (com senha e motivo)
 */
export const CancelItemModal = ({
  target, cart,
  reason, setReason,
  password, setPassword,
  error, setError,
  onCancel, onConfirm,
}) => {
  if (!target) return null;
  const it = cart.find(i => i.product_id === target && !i.cancelled);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...overlayClose(onCancel)}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="cancel-item-modal">
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Cancelar item</h3>
        <p className="text-sm text-slate-600 mb-3">
          {it && <>Item: <strong>{it.product_name}</strong> · {Number(it.quantity || 0)} × R$ {Number(it.unit_price || 0).toFixed(2)}</>}
        </p>
        <div className="space-y-2 mb-3">
          <Label>Motivo do cancelamento *</Label>
          <Input
            value={reason}
            onChange={(e) => { setReason(e.target.value); setError(''); }}
            placeholder="Ex: Cliente desistiu, peso errado, item avariado..."
            data-testid="cancel-item-reason"
            autoFocus
          />
          <Label className="pt-2 block">Senha do operador *</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && reason.trim().length >= 3 && password) {
                e.preventDefault();
                onConfirm();
              }
            }}
            placeholder="Sua senha"
            data-testid="cancel-item-password"
          />
          {error && (
            <p className="text-xs text-red-600 font-semibold" data-testid="cancel-item-error">{error}</p>
          )}
          <p className="text-xs text-slate-500">📝 Será registrado em Vendas Canceladas. Pressione Enter para confirmar.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="flex-1">Voltar</Button>
          <Button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-item">
            Confirmar cancelamento
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Modal: cancelar venda inteira (com motivo)
 */
export const CancelSaleModal = ({ show, cart, reason, setReason, password, setPassword, error, onClose, onConfirm }) => {
  if (!show) return null;
  const submitOnEnter = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onConfirm(); }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...overlayClose(onClose)}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="cancel-sale-modal">
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Cancelar Venda?</h3>
        <p className="text-sm text-slate-600 mb-3">
          Esta ação esvazia o carrinho e descarta a venda atual. Os itens NÃO serão vendidos.
          <br />
          <strong>{cart.filter(i => !i.cancelled).length}</strong> item(ns) será(ão) removido(s).
        </p>
        <div className="space-y-1 mb-3">
          <Label className="text-xs uppercase tracking-wider font-bold">Motivo do cancelamento *</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={submitOnEnter}
            placeholder="Ex.: Cliente desistiu, erro no operador, troca por outro produto..."
            autoFocus
            data-testid="cancel-sale-reason"
          />
        </div>
        <div className="space-y-1 mb-3">
          <Label className="text-xs uppercase tracking-wider font-bold">Senha do operador *</Label>
          <Input
            type="password"
            value={password || ''}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={submitOnEnter}
            placeholder="Digite sua senha para confirmar"
            data-testid="cancel-sale-password"
          />
          <p className="text-[11px] text-slate-500">Pressione <kbd className="px-1.5 py-0.5 bg-slate-100 rounded">Enter</kbd> para confirmar o cancelamento.</p>
        </div>
        {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Voltar</Button>
          <Button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-sale">
            Sim, cancelar venda
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Modal: bloqueio de navegação enquanto há itens no carrinho
 */
export const NavBlockModal = ({ show, cart, onStay, onCancelSale }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full" data-testid="nav-block-modal">
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Venda em andamento</h3>
        <p className="text-sm text-slate-600 mb-4">
          Você tem <strong>{cart.filter(i => !i.cancelled).length} item(ns)</strong> no carrinho.
          <br /><br />
          Você não pode sair do PDV antes de <strong>Finalizar</strong> ou <strong>Cancelar</strong> a venda.
        </p>
        <div className="flex flex-col gap-2">
          <Button onClick={onStay} className="bg-blue-600 hover:bg-blue-700" data-testid="nav-stay">
            Voltar para a venda
          </Button>
          <Button
            onClick={onCancelSale}
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-50"
            data-testid="nav-cancel-sale"
          >
            <Trash2 size={16} className="mr-2" /> Cancelar a venda e sair
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Modal: aplicar desconto — com justificativa OBRIGATÓRIA.
 */
export const DISCOUNT_REASON_PRESETS = [
  'Cliente Fidelidade',
  'Produto com Avaria',
  'Promoção do Dia',
  'Cliente Antigo',
  'Fim de Estoque',
  'Erro no Preço',
  'Cortesia da Casa',
  'Negociação com Cliente',
];

export const DiscountModal = ({
  show, discountInput, setDiscountInput,
  discountReason, setDiscountReason,
  discount, calculateSubtotal,
  applyDiscount, onClearAndClose, error, onClose,
}) => {
  const applyBtnRef = useRef(null);
  const reasonRef = useRef(null);
  if (!show) return null;
  const val = parseFloat(discountInput);
  const needsReason = !isNaN(val) && val > 0;
  const reasonOk = !needsReason || (discountReason && discountReason.trim().length >= 3);
  // Fluxo por teclado: valor → Enter → justificativa (preset ou texto) → foco vai ao "Aplicar" → Enter aplica e fecha
  const focusApply = () => setTimeout(() => applyBtnRef.current?.focus(), 0);
  const onValueKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!needsReason) { applyDiscount(); return; }
    if (reasonOk) focusApply(); else reasonRef.current?.focus();
  };
  const onReasonKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (reasonOk) focusApply();
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2" {...overlayClose(onClose)}>
      <div className="bg-white rounded-lg p-5 w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="discount-modal">
        <h3 className="text-xl font-bold text-slate-900 mb-2">Aplicar Desconto</h3>
        <p className="text-sm text-slate-600 mb-4">Subtotal: <strong>R$ {calculateSubtotal().toFixed(2)}</strong></p>
        <div className="space-y-2 mb-4">
          <Label>Valor do Desconto (R$)</Label>
          <Input
            type="number"
            step="0.01"
            value={discountInput}
            onChange={(e) => setDiscountInput(e.target.value)}
            placeholder={discount.toFixed(2)}
            autoFocus
            onKeyDown={onValueKeyDown}
            data-testid="discount-input"
          />
        </div>

        {needsReason && (
          <div className="space-y-2 mb-4" data-testid="discount-reason-block">
            <Label className="font-bold text-slate-700">
              Justificativa <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-1.5">
              {DISCOUNT_REASON_PRESETS.map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => { setDiscountReason(preset); focusApply(); }}
                  className={`text-[11px] font-medium py-1.5 px-2 rounded border transition-colors ${
                    discountReason === preset
                      ? 'bg-yellow-500 text-slate-900 border-yellow-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-yellow-50 hover:border-yellow-400'
                  }`}
                  data-testid={`discount-preset-${preset.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <Input
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              placeholder="Ou digite uma justificativa livre..."
              className="mt-2"
              ref={reasonRef}
              onKeyDown={onReasonKeyDown}
              data-testid="discount-reason-input"
            />
            <p className="text-[10px] text-slate-500">
              Escolha uma opção acima ou escreva um motivo (mín. 3 caracteres). Registrado no relatório de descontos.
            </p>
          </div>
        )}

        {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
          <Button onClick={onClearAndClose} variant="outline" className="flex-1">Remover</Button>
          <Button
            ref={applyBtnRef}
            onClick={applyDiscount}
            disabled={!reasonOk}
            className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-slate-900 focus:ring-4 focus:ring-yellow-300 focus:outline-none"
            data-testid="apply-discount"
          >
            Aplicar
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Modal: estornar última venda (F9)
 */
export const ReverseLastModal = ({
  show, preview, reason, setReason,
  loading, error, onClose, onConfirm,
}) => {
  if (!show || !preview) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="reverse-last-modal">
      <div className="bg-white rounded-lg p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <RotateCcw className="text-orange-600" size={28} />
          <h3 className="text-2xl font-bold text-slate-900">Estornar Última Venda</h3>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-4 space-y-1 text-sm">
          <p><strong>Venda nº:</strong> #{preview.sale_number}</p>
          <p><strong>Total:</strong> R$ {Number(preview.total).toFixed(2)}</p>
          <p><strong>Pagamento:</strong> {preview.payment_method}</p>
          <p><strong>Itens:</strong> {(preview.items || []).filter(i => !i.cancelled).length}</p>
          {preview.created_at && (
            <p className="text-xs text-slate-600">Emitida em: {new Date(preview.created_at).toLocaleString('pt-BR')}</p>
          )}
        </div>
        <div className="bg-yellow-50 border border-yellow-300 rounded-md p-3 mb-4 text-xs text-yellow-900">
          <strong>Atenção:</strong> A venda será cancelada (estoque será reposto e o valor estornado do fluxo de caixa). Os itens serão carregados no carrinho atual para correção.
        </div>
        <div className="space-y-2 mb-4">
          <Label>Motivo do estorno *</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: Erro de digitação, item errado, valor incorreto..."
            autoFocus
            data-testid="reverse-last-reason"
          />
        </div>
        {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={loading} data-testid="reverse-last-cancel">
            Voltar
          </Button>
          <Button onClick={onConfirm} disabled={loading} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" data-testid="reverse-last-confirm">
            {loading ? 'Estornando...' : 'Confirmar estorno'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Modal: PIX QR Code — Sicoob (cobrança dinâmica + confirmação automática) ou QR estático
 */
export const PIX_QR_TIMEOUT_SECS = 120;

export const PixModal = ({ pixData, onClose, onCancel }) => {
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [left, setLeft] = useState(PIX_QR_TIMEOUT_SECS);
  const isSicoob = pixData?.provider === 'sicoob';
  // "pago" só vale para a cobrança ATUAL (txid igual) — nunca reaproveita o status da venda anterior
  const paid = !!(status?.paid && status?.txid && status.txid === pixData?.txid);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Nova cobrança (novo txid) → zera status, contador regressivo e "copiado"
  useEffect(() => { setStatus(null); setLeft(PIX_QR_TIMEOUT_SECS); setCopied(false); }, [pixData?.txid, pixData?.order_id]);

  useEffect(() => {
    if (!pixData || !isSicoob || paid) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        // A cada 3s reconsulta GET /cob/{txid} no Sicoob (refresh=1); o webhook também atualiza o banco em paralelo
        const r = await axios.get(`${API}/pix/sicoob/charge/${pixData.txid}?refresh=1`, { withCredentials: true });
        if (stop || r.data?.txid !== pixData.txid) return;
        setStatus(r.data);
      } catch (_e) { /* tenta de novo no próximo ciclo */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { stop = true; clearInterval(id); };
  }, [pixData, isSicoob, paid, API]);

  // Contador regressivo de 2 min: expirou sem pagamento → fecha e volta para a venda
  useEffect(() => {
    if (!pixData || paid) return undefined;
    const t = setInterval(() => setLeft((x) => (x > 0 ? x - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [pixData, paid]);

  useEffect(() => {
    if (!pixData || paid || left > 0) return undefined;
    onCancel?.('expirado');
    return undefined;
  }, [left, paid, pixData, onCancel]);

  useEffect(() => {
    if (!paid) return undefined;
    const t = setTimeout(() => onClose(true), 2500);
    return () => clearTimeout(t);
  }, [paid, onClose]);

  if (!pixData) return null;
  const copy = async () => { try { await navigator.clipboard.writeText(pixData.qr_code); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_e) { /* sem clipboard */ } };
  const mm = String(Math.floor(left / 60)).padStart(2, '0'); const ss = String(left % 60).padStart(2, '0');
  const urgent = left <= 30;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="pix-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-2xl font-bold text-slate-900 mb-1 text-center">Pagamento PIX</h3>
        <p className="text-center text-3xl font-black text-emerald-700 mb-1" data-testid="pix-amount">R$ {Number(pixData.amount || 0).toFixed(2)}</p>
        {pixData.mixed && <p className="text-center text-xs text-slate-500 mb-3" data-testid="pix-mixed-hint">Pagamento misto: parte em PIX · total da venda R$ {Number(pixData.total || 0).toFixed(2)}</p>}
        {paid ? (
          <div className="bg-emerald-50 border-2 border-emerald-400 rounded-md p-6 mb-4 text-center" data-testid="pix-paid">
            <p className="text-2xl font-black text-emerald-700">✓ PIX recebido!</p>
            <p className="text-xs text-emerald-800 mt-1">Confirmado pelo Sicoob{status?.paid_at ? ` às ${new Date(status.paid_at).toLocaleTimeString('pt-BR')}` : ''}. Fechando…</p>
          </div>
        ) : (
          <>
            <div className="flex justify-center mb-3">
              {/* Fundo e borda SEMPRE brancos (independe do tema) — garante leitura do QR no tema escuro */}
              <div className="pix-qr-box" data-testid="pix-qr-box">
                <QRCode value={pixData.qr_code} size={200} bgColor="#FFFFFF" fgColor="#000000" />
              </div>
            </div>
            <div className={`${urgent ? 'bg-red-50 border-red-300 text-red-700' : 'bg-slate-50 border-slate-200 text-slate-700'} border rounded-md px-3 py-1.5 mb-3 text-center`} data-testid="pix-countdown">
              <span className="text-xs uppercase font-bold">Tempo restante </span>
              <span className={`text-xl font-black tabular-nums ${urgent ? 'animate-pulse' : ''}`} data-testid="pix-countdown-value">{mm}:{ss}</span>
              <p className="text-[11px] mt-0.5">Ao expirar, a tela fecha e a venda volta para a escolha da forma de pagamento.</p>
            </div>
            <div className="mb-3">
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">PIX copia e cola</p>
              <div className="flex gap-2">
                <input readOnly value={pixData.qr_code} className="flex-1 text-[11px] font-mono border border-slate-300 rounded px-2 py-1 bg-slate-50 truncate" data-testid="pix-brcode" />
                <Button type="button" variant="outline" onClick={copy} className="h-8 px-3" data-testid="pix-copy">{copied ? 'Copiado!' : 'Copiar'}</Button>
              </div>
            </div>
            <div className={`${isSicoob ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'} border rounded-md p-3 mb-4 text-center text-sm`} data-testid="pix-status">
              {isSicoob ? (
                <>
                  <p className="font-bold flex items-center justify-center gap-2"><span className="inline-block w-2 h-2 rounded-full bg-blue-600 animate-pulse" /> Aguardando pagamento…</p>
                  <p className="text-xs mt-1">Cobrança Sicoob {status?.status || pixData.status || 'ATIVA'} · a confirmação é automática assim que o cliente pagar.</p>
                </>
              ) : (
                <>
                  <p><strong>Chave PIX:</strong> {pixData.pix_key}</p>
                  <p className="text-xs mt-1 font-bold">QR ESTÁTICO — sem confirmação automática. Confira o recebimento no app do banco.</p>
                  <p className="text-[11px] mt-1">Para baixa automática, ative o PIX Sicoob em Configurações → PIX (certificado .pfx + chave PIX + "Ativado no PDV").</p>
                </>
              )}
            </div>
          </>
        )}
        {paid ? (
          <Button onClick={() => onClose(true)} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="close-pix-modal">Concluir</Button>
        ) : (
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onCancel?.('cancelado')} className="flex-1 border-red-300 text-red-700 hover:bg-red-50" data-testid="cancel-pix-modal">Cancelar / trocar forma</Button>
            <Button onClick={() => onClose(false)} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="close-pix-modal">{isSicoob ? 'Registrar (confirmar depois)' : 'Pago — registrar venda'}</Button>
          </div>
        )}
      </div>
    </div>
  );
};
